'use client'

import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { getTokenImage } from '@/ui/assets/tokens'
import Image from 'next/image'
import { useEffect, useMemo, useState } from 'react'
import { buildTokenImageSrcChain } from './tokenImageFallback'

interface TokenImageProps {
  src: string
  alt: string
  width: number
  height: number
  className?: string
  fallbackSrc?: string
  tokenAddress?: string
  withBackground?: boolean
}

const DEFAULT_FALLBACK = '/tokens/unknown.svg'

export function TokenImage({
  src,
  alt,
  width,
  height,
  className,
  fallbackSrc,
  tokenAddress,
  withBackground = false,
}: TokenImageProps) {
  const srcChain = useMemo(() => {
    return buildTokenImageSrcChain({
      src,
      fallbackSrc,
      tokenAddress,
      defaultFallbackSrc: DEFAULT_FALLBACK,
    })
  }, [src, fallbackSrc, tokenAddress])
  const srcChainKey = srcChain.join('|')

  const [srcIndex, setSrcIndex] = useState(0)

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset fallback chain when source inputs change
  useEffect(() => {
    setSrcIndex(0)
  }, [srcChainKey])

  const handleError = () => {
    setSrcIndex((current) => Math.min(current + 1, srcChain.length - 1))
  }

  const currentSrc = srcChain[srcIndex] ?? DEFAULT_FALLBACK

  const image = (
    <Image
      src={currentSrc}
      alt={alt}
      width={width}
      height={height}
      className={withBackground ? 'rounded-full' : className}
      onError={handleError}
      unoptimized={currentSrc.startsWith('http')}
    />
  )

  if (withBackground) {
    return (
      <div
        className={`flex items-center justify-center overflow-hidden rounded-full bg-neutral-800 ${className ?? ''}`}
        style={{ width, height }}
      >
        {image}
      </div>
    )
  }

  return image
}

interface TokenImageBySymbolProps {
  symbol: string
  width: number
  height: number
  className?: string
  logoURI?: string
  tokenAddress?: string
  withBackground?: boolean
}

export function TokenImageBySymbol({
  symbol,
  width,
  height,
  className,
  logoURI,
  tokenAddress,
  withBackground,
}: TokenImageBySymbolProps) {
  const primarySrc = getTokenImage(TokenSymbol(symbol))
  const fallbackSrc = logoURI || DEFAULT_FALLBACK

  return (
    <TokenImage
      src={primarySrc}
      alt={symbol}
      width={width}
      height={height}
      className={className}
      fallbackSrc={fallbackSrc}
      tokenAddress={tokenAddress}
      withBackground={withBackground}
    />
  )
}
